import { Listbox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { FC, Fragment } from 'react';

import ChevronRight from '../svgs/ChevronRight';
import { XIcon } from '@heroicons/react/outline';

type OptionObject = { label: string; value: string };

interface SelectMenuProps<T> {
  onChange: (...event: unknown[]) => void;
  fieldValue: T;
  // You can pass either an array of type T or an array of OptionObject
  fieldOptions?: (T | OptionObject)[];
  // Option to include header'd sections of options within the select menu
  fieldOptionSections?: {
    sectionHeader: string;
    fieldOptions: T[];
  }[];
  SelectOptionComponent?: FC<{
    value: T;
  }>;
  containerClassName?: string;
  optionsContainerClassName?: string;
  buttonClassName?: string;
  label: string;
  smallLabel?: boolean;
  hideLabel?: boolean;
  placeholder?: string;
  disabled?: boolean;
  autofocus?: boolean;
  selectedPrefix?: string;
  onClear?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SelectMenu: FC<SelectMenuProps<any>> = ({
  onChange,
  fieldValue,
  fieldOptions,
  fieldOptionSections,
  SelectOptionComponent,
  containerClassName = '',
  optionsContainerClassName = '',
  buttonClassName = ',',
  label,
  smallLabel = false,
  hideLabel = false,
  placeholder = 'Select',
  disabled = false,
  autofocus = false,
  selectedPrefix,
  onClear,
}) => {
  const getDisplayValue = (value: any) => {
    if (!value) return '';

    if (
      fieldOptions &&
      fieldOptions.some(
        (option) => typeof option === 'object' && 'label' in option,
      )
    ) {
      const option = fieldOptions.find(
        (option) => typeof option === 'object' && option.value === value,
      );
      return option ? option.label : value;
    }
    return value;
  };

  const getOptionValue = (option: any) => {
    if (!option) return '';
    return typeof option === 'object' && 'value' in option
      ? option.value
      : option;
  };

  const handleChange = (value: any) => {
    const optionValue = getOptionValue(value);
    onChange(optionValue);
  };

  return (
    <Listbox value={fieldValue} onChange={handleChange} disabled={disabled}>
      {({ open }) => (
        <div className={containerClassName}>
          <Listbox.Label
            className={classNames(
              'mb-1 block text-secondary-125',
              {
                'text-small-action': smallLabel,
                'text-caption': !smallLabel,
              },
              hideLabel && 'hidden',
            )}
          >
            {label}
          </Listbox.Label>
          <div className="relative w-full">
            <Listbox.Button
              className={classNames(
                'relative flex w-full cursor-default flex-row items-center justify-start',
                'max-h-14 rounded-md bg-neutral-50 py-4 pl-3 pr-10 text-left shadow-sm sm:text-caption',
                'focus:border-secondary-50 focus:outline-none focus:ring-2 focus:ring-secondary-50',
                onClear && Boolean(fieldValue) && 'pr-20',
                buttonClassName,
              )}
              autoFocus={autofocus}
            >
              {Boolean(fieldValue) ? (
                <div
                  className={classNames(
                    'flex min-w-0 items-center truncate pl-2',
                    optionsContainerClassName,
                  )}
                >
                  {selectedPrefix && (
                    <span className="mr-1 flex-shrink-0 text-caption font-bold">
                      {selectedPrefix}
                    </span>
                  )}
                  {SelectOptionComponent ? (
                    <SelectOptionComponent value={fieldValue} />
                  ) : (
                    <span className="truncate text-caption">
                      {getDisplayValue(fieldValue)}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-caption text-neutral-125/75">
                  {placeholder}
                </span>
              )}
              {!disabled && (
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronRight className="rotate-90" />
                </span>
              )}
            </Listbox.Button>
            {onClear && Boolean(fieldValue) && (
              <button
                type="button"
                aria-label="Clear selection"
                className="absolute inset-y-0 right-10 z-10 flex items-center px-1 text-neutral-125 hover:text-neutral-150"
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onClear();
                }}
              >
                <XIcon className="h-4 w-4" />
              </button>
            )}

            <Transition
              show={open}
              as={Fragment}
              enter="ease-in duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
            >
              <Listbox.Options className="text-base absolute z-[100] mt-1 max-h-[400px] w-full list-none overflow-auto rounded-md bg-white py-1 pl-0 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-caption">
                {fieldOptions?.map((fieldOption, index) => (
                  <Listbox.Option
                    key={`${index}_selectMenu`}
                    className={({ active }) =>
                      classNames(
                        'relative flex max-h-12 cursor-default flex-row items-center py-4 pl-5 pr-4',
                        active && 'bg-secondary-20',
                      )
                    }
                    value={getOptionValue(fieldOption)}
                  >
                    {SelectOptionComponent ? (
                      <div className="text-neutral-150">
                        <SelectOptionComponent value={fieldOption} />
                      </div>
                    ) : (
                      <span className="text-neutral-150">
                        {getDisplayValue(getOptionValue(fieldOption))}
                      </span>
                    )}
                  </Listbox.Option>
                ))}
                {fieldOptionSections?.map(
                  (fieldOptionSection, sectionIndex) => (
                    <div key={`${sectionIndex}_selectMenuSection`}>
                      <div
                        className={classNames(
                          'px-4 pb-1 pt-3 text-small-caption font-bold text-green-150',
                          sectionIndex !== 0 && 'border-t border-neutral-50',
                        )}
                      >
                        {fieldOptionSection.sectionHeader}
                      </div>
                      {fieldOptionSection.fieldOptions?.map(
                        (fieldOption, index) => (
                          <Listbox.Option
                            key={`${sectionIndex}_${index}_selectMenuOption`}
                            className={({ active }) =>
                              classNames(
                                'relative flex max-h-12 cursor-default flex-row items-center truncate p-4',
                                active && 'bg-neutral-25',
                              )
                            }
                            value={getOptionValue(fieldOption)}
                          >
                            {SelectOptionComponent ? (
                              <div className="text-neutral-150">
                                <SelectOptionComponent value={fieldOption} />
                              </div>
                            ) : (
                              <span className="text-neutral-150">
                                {getDisplayValue(getOptionValue(fieldOption))}
                              </span>
                            )}
                          </Listbox.Option>
                        ),
                      )}
                    </div>
                  ),
                )}
              </Listbox.Options>
            </Transition>
          </div>
        </div>
      )}
    </Listbox>
  );
};

export default SelectMenu;
